import React, { useEffect, useState, useRef } from 'react';
import { getSubjects, addSubject } from '../services/api';
import './SubjectSelector.css';

/**
 * SubjectSelector
 *
 * Props:
 *  value       — current selected subject (string, single mode)
 *  values      — current selected subjects (array, multi mode)
 *  onChange    — (subject: string) => void   (single)
 *  onChangeMulti — (subjects: string[]) => void  (multi)
 *  multi       — enable multi-select mode
 *  placeholder — input placeholder
 *  allowNew    — show "add new" option (default: true)
 */
export default function SubjectSelector({
  value, values = [], onChange, onChangeMulti,
  multi = false, placeholder = 'اختر المادة أو أضف جديدة',
  allowNew = true, className = '',
}) {
  const [subjects,    setSubjects]    = useState([]);
  const [search,      setSearch]      = useState('');
  const [open,        setOpen]        = useState(false);
  const [adding,      setAdding]      = useState(false);
  const [newSubject,  setNewSubject]  = useState('');
  const containerRef = useRef(null);

  // Load subjects on mount
  useEffect(() => {
    getSubjects()
      .then(r => setSubjects(r.data.subjects || []))
      .catch(() => {});
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = subjects.filter(s =>
    s.includes(search) || s.toLowerCase().includes(search.toLowerCase())
  );

  const isSelected = (s) => multi ? values.includes(s) : value === s;

  const select = (s) => {
    if (multi) {
      const next = values.includes(s)
        ? values.filter(v => v !== s)
        : [...values, s];
      onChangeMulti?.(next);
    } else {
      onChange?.(s);
      setOpen(false);
      setSearch('');
    }
  };

  const handleAddNew = async () => {
    const name = newSubject.trim();
    if (name.length < 2) return;
    setAdding(true);
    try {
      await addSubject(name);
      const r = await getSubjects();
      setSubjects(r.data.subjects || []);
      select(name);
      setNewSubject('');
      setSearch('');
    } catch (err) {
      console.error('Failed to add subject', err);
    }
    setAdding(false);
  };

  const displayValue = multi
    ? values.length > 0 ? values.join('، ') : ''
    : value || '';

  return (
    <div className={`subject-selector ${className}`} ref={containerRef}>
      {/* Trigger */}
      <div
        className={`ss-trigger ${open ? 'ss-open' : ''}`}
        onClick={() => setOpen(!open)}
        role="button"
        tabIndex={0}
        onKeyDown={e => e.key === 'Enter' && setOpen(!open)}
      >
        {multi && values.length > 0 ? (
          <div className="ss-tags">
            {values.map(v => (
              <span key={v} className="ss-tag">
                {v}
                <button
                  type="button"
                  className="ss-tag-remove"
                  onClick={e => { e.stopPropagation(); select(v); }}
                >×</button>
              </span>
            ))}
          </div>
        ) : (
          <span className={displayValue ? 'ss-value' : 'ss-placeholder'}>
            {displayValue || placeholder}
          </span>
        )}
        <span className={`ss-arrow ${open ? 'ss-arrow-up' : ''}`}>▾</span>
      </div>

      {/* Dropdown */}
      {open && (
        <div className="ss-dropdown">
          {/* Search */}
          <div className="ss-search-wrap">
            <input
              className="ss-search"
              placeholder="🔍 ابحث عن مادة..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              autoFocus
            />
          </div>

          {/* List */}
          <div className="ss-list">
            {filtered.length === 0 && !search && (
              <div className="ss-empty">لا توجد مواد</div>
            )}
            {filtered.map(s => (
              <button
                key={s}
                type="button"
                className={`ss-item ${isSelected(s) ? 'ss-item-selected' : ''}`}
                onClick={() => select(s)}
              >
                {multi && <span className="ss-checkbox">{isSelected(s) ? '☑' : '☐'}</span>}
                {s}
                {isSelected(s) && !multi && <span className="ss-check">✓</span>}
              </button>
            ))}

            {/* Add new subject */}
            {allowNew && search.length >= 2 && !filtered.some(s => s === search) && (
              <button
                type="button"
                className="ss-add-new"
                onClick={() => { setNewSubject(search); handleAddNew(); }}
                disabled={adding}
              >
                {adding
                  ? <span>جاري الإضافة...</span>
                  : <><span className="ss-add-icon">＋</span> إضافة "<strong>{search}</strong>" كمادة جديدة</>
                }
              </button>
            )}
          </div>

          {/* Manual add */}
          {allowNew && (
            <div className="ss-add-manual">
              <input
                className="ss-search"
                placeholder="✏️ أضف مادة جديدة..."
                value={newSubject}
                onChange={e => setNewSubject(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleAddNew()}
              />
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={handleAddNew}
                disabled={adding || newSubject.trim().length < 2}
                style={{ flexShrink: 0 }}
              >
                {adding ? '...' : 'إضافة'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
